import { cn } from '#src/styling/cn.ts';
import type { Icon } from '@phosphor-icons/react';
import { CheckCircleIcon, InfoIcon, WarningIcon } from '@phosphor-icons/react';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';

const alertVariants = cva('flex gap-3 rounded-lg p-4', {
  variants: {
    variant: {
      error: 'bg-red-50 text-red-700',
      warning: 'bg-amber-50 text-amber-700',
      info: 'bg-blue-50 text-blue-600',
      success: 'bg-green-50 text-green-700',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

export type AlertProps = ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    title?: string;
    children?: ReactNode;
  };

const VARIANT_ICONS: Record<NonNullable<AlertProps['variant']>, Icon> = {
  error: WarningIcon,
  warning: WarningIcon,
  info: InfoIcon,
  success: CheckCircleIcon,
};

export const Alert = ({ className, variant = 'info', title, children, ...props }: AlertProps) => {
  const Icon = VARIANT_ICONS[variant ?? 'info'];
  // manually aligning icon to look good with when there is only title or only children
  const iconClassName = title ? 'mt-0.5 size-5 shrink-0' : '-mt-px size-5 shrink-0';

  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      <Icon className={iconClassName} aria-hidden="true" />
      <div className="flex-1 space-y-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className="text-sm">{children}</div>}
      </div>
    </div>
  );
};
